/** Shared DOM id + style inlining for client-side quote PDF/HTML export. */
export const QUOTE_PDF_CONTAINER_CLASS = "quote-container";

let quotePdfIdSeq = 0;

export function createQuotePdfContainerId(prefix = "pdf-quote") {
  quotePdfIdSeq += 1;
  return `${prefix}-${Date.now()}-${quotePdfIdSeq}`;
}

/** Rewrites scoped `#containerId` rules for standalone HTML export (`.quote-container`). */
export function inlineQuotePdfStylesForExport(styleText, containerId) {
  if (!containerId) return String(styleText ?? "");
  return String(styleText).replace(
    new RegExp(`#${containerId}`, "g"),
    `.${QUOTE_PDF_CONTAINER_CLASS}`,
  );
}

/**
 * Exact CSS used by vacation-package quote PDF (PackageDetails.generateQuoteHTML).
 * Keep in sync with packages PDF design — Belvedere campaign reuses this 1:1.
 */
export function buildVacationPackageQuotePdfCss(containerSelector) {
  const s = containerSelector;
  return `
        ${s} {
          direction: rtl;
          font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
          background: #ffffff;
          width: 794px;
        }
        ${s} .header {
          background: #ffffff;
          color: #1a202c;
          padding: 2px 10px 4px 10px;
          text-align: center;
          margin-bottom: 2px;
          border-bottom: 2px solid #004E89;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        ${s} .header .logo {
          width: auto;
          max-width: 150px;
          height: auto;
          max-height: 45px;
          object-fit: contain;
          display: block;
          margin: 0 auto 2px auto;
          vertical-align: middle;
        }
        ${s} .header h1 {
          font-size: 18px;
          margin: 1px 0;
          color: #1a202c;
          text-align: center;
          font-weight: 600;
        }
        ${s} .header .ref {
          font-size: 8px;
          background: #f8f9fa;
          padding: 1px 5px;
          border-radius: 6px;
          display: inline-block;
          margin-top: 0px;
          color: #1a202c;
          font-weight: 600;
          border: 1px solid #e2e8f0;
        }
        ${s} .content {
          padding: 2px 10px;
          margin: 0;
          background: #ffffff;
        }
        ${s} .section {
          margin: 0;
          margin-bottom: 2px;
          padding: 0;
        }
        ${s} .info-section {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          padding: 3px;
          margin: 0 0 3px 0;
        }
        ${s} .info-section-title {
          font-size: 10px;
          font-weight: 700;
          color: #FF6B35;
          margin-bottom: 2px;
          padding-bottom: 2px;
          border-bottom: 2px solid #004E89;
          text-align: right;
        }
        ${s} .card {
          background: #f8f9fa;
          border: none;
          border-right: 3px solid #004E89;
          padding: 3px 6px;
          margin: 0 0 3px 0;
          border-radius: 3px;
        }
        ${s} .card:last-child {
          margin-bottom: 0;
        }
        ${s} .info-row {
          margin: 2px 0;
          padding: 2px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        ${s} .info-label {
          font-weight: 600;
          font-size: 9pt;
          color: #4a5568;
          min-width: 90px;
        }
        ${s} .info-value {
          font-size: 9pt;
          color: #1a202c;
          font-weight: 500;
          text-align: right;
          flex: 1;
        }
        ${s} .dates {
          display: flex;
          gap: 4px;
          margin: 4px 0;
        }
        ${s} .date-box {
          flex: 1;
          background: linear-gradient(135deg, #FF6B35 0%, #004E89 100%);
          border: none;
          padding: 5px;
          text-align: center;
          border-radius: 4px;
          color: white;
        }
        ${s} .date-label {
          font-size: 8pt;
          color: rgba(255,255,255,0.9);
          margin-bottom: 2px;
          font-weight: 500;
        }
        ${s} .date-value {
          font-size: 10pt;
          font-weight: 700;
          color: white;
        }
        ${s} .map-container {
          margin: 4px 0;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          overflow: hidden;
          background: #ffffff;
        }
        ${s} .map-image {
          width: 100%;
          height: auto;
          display: block;
          max-height: 120px;
          min-height: 100px;
          object-fit: cover;
          background: #f0f0f0;
        }
        ${s} .map-title {
          background: linear-gradient(135deg, #FF6B35 0%, #004E89 100%);
          color: white;
          padding: 4px;
          text-align: center;
          font-size: 10px;
          font-weight: 700;
        }
        ${s} .terms-section {
          background: #f7fafc;
          border-radius: 6px;
          padding: 5px;
          margin: 4px 0;
          border: 1px solid #e2e8f0;
        }
        ${s} .terms-title {
          font-size: 11px;
          font-weight: 700;
          color: #FF6B35;
          margin-bottom: 4px;
          text-align: center;
          padding-bottom: 3px;
          border-bottom: 2px solid #004E89;
        }
        ${s} .terms-content {
          color: #4a5568;
          line-height: 1.3;
          font-size: 8pt;
          text-align: right;
        }
        ${s} .terms-content p {
          margin-bottom: 2px;
        }
        ${s} .footer {
          background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%);
          color: white;
          padding: 5px;
          margin-top: 4px;
          text-align: center;
        }
        ${s} .footer-message {
          font-size: 10px;
          margin-bottom: 2px;
          font-weight: 600;
        }
        ${s} .support-info {
          font-size: 8px;
          color: rgba(255,255,255,0.85);
          line-height: 1.3;
        }
        ${s} .price-highlight {
          background: linear-gradient(135deg, #2ECC71 0%, #27AE60 100%);
          border: none;
          border-radius: 4px;
          padding: 6px 8px;
          margin-top: 4px;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        ${s} .price-highlight .info-row {
          margin: 0;
          padding: 0;
        }
        ${s} .price-highlight .info-label {
          color: white;
          font-size: 9pt;
          font-weight: 600;
        }
        ${s} .price-highlight .info-value {
          color: white;
          font-size: 12pt;
          font-weight: 700;
        }
        ${s} .info-section:has(.price-highlight) {
          page-break-inside: avoid;
          break-inside: avoid;
        }
      `;
}

/** Standalone print-document wrapper styles (same as packages quote PDF). */
export function buildVacationPackageQuotePdfDocumentCss(scopedCss) {
  return `
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    body {
      font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      direction: rtl;
      background: #f5f5f5;
      padding: 5px;
    }
    ${scopedCss}
    .${QUOTE_PDF_CONTAINER_CLASS} {
      direction: rtl;
      font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      width: 794px;
      background: white;
      padding: 5px;
      box-sizing: border-box;
      margin: 0 auto;
    }
    .${QUOTE_PDF_CONTAINER_CLASS} .info-section:has(.price-highlight) {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .${QUOTE_PDF_CONTAINER_CLASS} .price-highlight {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    @media print {
      body {
        background: white;
        padding: 0;
      }
      .${QUOTE_PDF_CONTAINER_CLASS} {
        width: 210mm;
        min-height: 297mm;
        max-width: 210mm;
        margin: 0 auto;
        padding: 10px;
      }
      .${QUOTE_PDF_CONTAINER_CLASS} .info-section:has(.price-highlight) {
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .${QUOTE_PDF_CONTAINER_CLASS} .price-highlight {
        page-break-inside: avoid;
        break-inside: avoid;
      }
    }
  `;
}
